import { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RefreshCw, Trash2, Search } from 'lucide-react';
import { logger } from '@/lib/logger';
import { QueueStatusCards } from '@/components/dashboard/QueueStatusCards';
import { QueueVolumeChart } from '@/components/dashboard/QueueVolumeChart';
import { QueueItemsTable } from '@/components/dashboard/QueueItemsTable';
import { QueueHealthAlerts } from '@/components/dashboard/QueueHealthAlerts';
import { QueueProviderDistribution } from '@/components/dashboard/QueueProviderDistribution';
import { PaginationFooter } from '@/components/ui/pagination-footer';
// Usando hooks PGMQ em vez dos hooks legados
import { 
  useFlowQueueStatsPgmq,
  useFlowQueueItemsPgmq,
  useFlowQueueVolumePgmq,
  useFlowQueueAlertsPgmq,
} from '@/hooks/useFlowQueueMonitorPgmq';
import { useDuplicatedConversations } from '@/hooks/useQueueMonitor';
import { FlowQueueSlowestItems } from '@/components/dashboard/FlowQueueSlowestItems';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Página de monitoramento da fila de processamento de flows
 * 
 * Disponível apenas para Admin Root
 * Exibe métricas em tempo real, gráficos de volume, tabela de itens e alertas de saúde
 */
export default function QueueMonitor() {
  // Estados de filtro
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [flowFilter, setFlowFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [attemptsFilter, setAttemptsFilter] = useState('all');
  const [duplicFilter, setDuplicFilter] = useState('all');
  const [phoneSearch, setPhoneSearch] = useState(''); // Pesquisa por telefone
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // Opções de período para limpeza de itens (em horas)
  const cleanupOptions = [
    { value: '0', label: 'Tudo', description: 'todos os itens arquivados' },
    { value: '1', label: 'Última 1h', description: 'mais de 1 hora' },
    { value: '24', label: '1 dia', description: 'mais de 1 dia' },
    { value: '168', label: '7 dias', description: 'mais de 7 dias' },
    { value: '360', label: '15 dias', description: 'mais de 15 dias' },
    { value: '720', label: '30 dias', description: 'mais de 30 dias' },
  ];
  const [cleanupPeriod, setCleanupPeriod] = useState('168'); // 7 dias padrão
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Buscar lista de tenants para dropdown
  const { data: tenants } = useQuery({
    queryKey: ['tenants-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, name')
        .order('name');
      return data || [];
    }
  });

  // Buscar lista de flows para dropdown
  const { data: flows } = useQuery({
    queryKey: ['flows-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('flows')
        .select('id, name')
        .order('name');
      return data || [];
    }
  });

  // Buscar dados da fila via PGMQ (limite maior para paginação no frontend)
  const { data: stats, isLoading: statsLoading } = useFlowQueueStatsPgmq();
  const { data: items, isLoading: itemsLoading } = useFlowQueueItemsPgmq(statusFilter, providerFilter, 500);
  const { data: volume, isLoading: volumeLoading } = useFlowQueueVolumePgmq(24);
  const { data: alerts, isLoading: alertsLoading } = useFlowQueueAlertsPgmq();
  const { data: duplicatedConversations } = useDuplicatedConversations();

  // Filtrar e paginar itens no frontend
  const { paginatedItems, totalFiltered } = useMemo(() => {
    if (!items) return { paginatedItems: [], totalFiltered: 0 };
    
    // Aplicar filtros adicionais
    let filtered = items;
    
    // Filtro por tenant
    if (tenantFilter !== 'all') {
      filtered = filtered.filter(item => item.tenant_id === tenantFilter);
    }
    
    // Filtro por flow (busca no payload)
    if (flowFilter !== 'all') {
      filtered = filtered.filter(item => {
        const payload = item.payload as Record<string, unknown>;
        return payload?.flow_id === flowFilter;
      });
    }
    
    // Filtro por tipo de queue
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.queue_type === typeFilter);
    }
    
    // Filtro por tentativas
    if (attemptsFilter !== 'all') {
      if (attemptsFilter === '3+') {
        filtered = filtered.filter(item => (item.attempts || 0) >= 3);
      } else {
        const attempts = parseInt(attemptsFilter);
        filtered = filtered.filter(item => item.attempts === attempts);
      }
    }
    
    // Filtro por duplicação
    if (duplicFilter !== 'all' && duplicatedConversations) {
      filtered = filtered.filter(item => {
        const payload = item.payload as Record<string, unknown>;
        const innerPayload = payload?.payload as Record<string, unknown> | undefined;
        const conversationId = innerPayload?.conversation_id as string | undefined;
        
        const hasDuplication = conversationId ? duplicatedConversations.has(conversationId) : false;
        
        if (duplicFilter === 'duplicated') {
          return hasDuplication;
        } else {
          return !hasDuplication;
        }
      });
    }
    
    // Filtro por telefone (pesquisa livre - remove não-dígitos para comparação)
    if (phoneSearch.trim()) {
      const searchTerm = phoneSearch.replace(/\D/g, '');
      filtered = filtered.filter(item => {
        const phone = (item.contact_phone || '').replace(/\D/g, '');
        return phone.includes(searchTerm);
      });
    }
    
    // Paginar
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, filtered.length);
    
    return {
      paginatedItems: filtered.slice(startIndex, endIndex),
      totalFiltered: filtered.length
    };
  }, [items, currentPage, pageSize, tenantFilter, flowFilter, typeFilter, attemptsFilter, duplicFilter, duplicatedConversations, phoneSearch]);

  // Resetar página ao mudar filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, providerFilter, tenantFilter, flowFilter, typeFilter, attemptsFilter, duplicFilter, phoneSearch]);

  // Estado de loading para refresh manual
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();

  // Função para refresh manual - invalida cache para forçar nova busca
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      // Invalida todas as queries PGMQ para forçar nova busca
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['flow-queue-stats-pgmq'] }),
        queryClient.invalidateQueries({ queryKey: ['flow-queue-items-pgmq'] }),
        queryClient.invalidateQueries({ queryKey: ['flow-queue-volume-pgmq'] }),
        queryClient.invalidateQueries({ queryKey: ['flow-queue-alerts-pgmq'] }),
        queryClient.invalidateQueries({ queryKey: ['flow-queue-slowest-pgmq'] }),
        queryClient.invalidateQueries({ queryKey: ['duplicated-conversations'] }),
      ]);
      setLastUpdate(new Date());
      toast.success('Dados atualizados');
    } catch (error) {
      logger.error('[QueueMonitor] Erro ao atualizar dados:', error);
      toast.error('Erro ao atualizar dados');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Função para limpar itens antigos da fila PGMQ
  const handleCleanup = async () => {
    try {
      const hoursToKeep = parseInt(cleanupPeriod);
      const selectedOption = cleanupOptions.find(o => o.value === cleanupPeriod);
      
      // Usar a nova RPC de limpeza PGMQ
      const { data: deletedCount, error } = await supabase.rpc('clean_old_pgmq_flow_items', { 
        hours_to_keep: hoursToKeep 
      });
      
      if (error) throw error;

      // Feedback informativo sobre quantidade deletada
      if (deletedCount === 0) {
        toast.info(`Nenhum item encontrado ${selectedOption?.description || 'nesse período'}`);
      } else {
        toast.success(`${deletedCount} itens removidos (${selectedOption?.description || 'antigos'})`);
      }
      handleRefresh();
    } catch (error) {
      logger.error('[QueueMonitor] Erro ao limpar fila:', error);
      toast.error('Erro ao limpar itens antigos');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Fila de Processamento</h1>
          <p className="text-muted-foreground">
            Monitoramento em tempo real do processamento de flows
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Última atualização: {formatDistanceToNow(lastUpdate, { 
              addSuffix: true,
              locale: ptBR 
            })}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1">
            <Select value={cleanupPeriod} onValueChange={setCleanupPeriod}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {cleanupOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleCleanup}>
              <Trash2 className="mr-2 h-4 w-4" />
              Limpar Antigos
            </Button>
          </div>
          <Button onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn("mr-2 h-4 w-4", isRefreshing && "animate-spin")} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <QueueStatusCards stats={stats} isLoading={statsLoading} />

      {/* Alertas de Saúde */}
      <QueueHealthAlerts alerts={alerts} isLoading={alertsLoading} />

      {/* Gráfico de Volume - linha inteira */}
      <QueueVolumeChart data={volume} isLoading={volumeLoading} />

      {/* Distribuição por Provider */}
      <QueueProviderDistribution />

      {/* Top 20 - Maior Tempo de Execução */}
      <FlowQueueSlowestItems />

      {/* Tabela de Itens com Filtros */}
      <div className="space-y-4">
        {/* Linha de filtros */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro Provider */}
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Providers</SelectItem>
              <SelectItem value="z-api">Z-API</SelectItem>
              <SelectItem value="evolution">Evolution</SelectItem>
              <SelectItem value="chatwoot">Chatwoot</SelectItem>
              <SelectItem value="wuzapi">WuzAPI</SelectItem>
              <SelectItem value="notificame">Notificame</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro Tenant */}
          <Select value={tenantFilter} onValueChange={setTenantFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tenant" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Tenants</SelectItem>
              {tenants?.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro Flow */}
          <Select value={flowFilter} onValueChange={setFlowFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Flow" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Flows</SelectItem>
              {flows?.map((flow) => (
                <SelectItem key={flow.id} value={flow.id}>
                  {flow.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Filtro Tipo */}
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos Tipos</SelectItem>
              <SelectItem value="start_flow">start_flow</SelectItem>
              <SelectItem value="process_response">process_response</SelectItem>
              <SelectItem value="retry_node">retry_node</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro Tentativas */}
          <Select value={attemptsFilter} onValueChange={setAttemptsFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Tentativas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="0">0 tentativas</SelectItem>
              <SelectItem value="1">1 tentativa</SelectItem>
              <SelectItem value="2">2 tentativas</SelectItem>
              <SelectItem value="3+">3+ tentativas</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtro Duplicações */}
          <Select value={duplicFilter} onValueChange={setDuplicFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Duplicações" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="duplicated">Com Duplicação</SelectItem>
              <SelectItem value="no-duplicated">Sem Duplicação</SelectItem>
            </SelectContent>
          </Select>

          {/* Campo de pesquisa por telefone */}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por telefone..."
              value={phoneSearch}
              onChange={(e) => setPhoneSearch(e.target.value)}
              className="w-[180px] pl-8"
            />
          </div>
        </div>

        <QueueItemsTable
          items={paginatedItems}
          isLoading={itemsLoading}
          isRefreshing={isRefreshing}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={handleRefresh}
          duplicatedConversations={duplicatedConversations}
        />

        {/* Paginação */}
        <PaginationFooter
          totalItems={totalFiltered}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[25, 50, 100, 200]}
        />
      </div>
    </div>
  );
}
